import type { AccessLevel, AccessLevelProp, ButtonProps, ControlSize } from '@meterup/atto';
import type { Dispatch, ReactNode } from 'react';
import {
  Alert,
  Button,
  colors,
  ControlGroup,
  darkThemeSelector,
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuGroup,
  DropdownMenuPopover,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  Icon,
  Segments,
  styled,
  Text,
  TextInput,
  VStack,
} from '@meterup/atto';
import { type DurationLike, DateTime, Duration } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { Timestamp } from '../../topics/time';
import { checkDefinedOrThrow } from '../../helpers/expectDefinedOrThrow';
import {
  formatDateTime,
  now,
  parseDateTime,
  stripDateForInput,
  stripDateTimeForInput,
  TimeFormat,
} from '../../topics/time';
import { FormatDateTime } from '../FormatDateTime';
import { useDateTimeRangeStateContextOrNull } from './DateTimeRangeStateContext';
import { useDateTimeRangeValue } from './DateTimeRangeStorageContext';
import {
  formatToISO,
  getDuration,
  getStep,
  IntervalUnits,
  intervalUnitsToSeconds,
  TimePeriod,
  timePeriodLabel,
} from './utils';

export interface UseDateTimeRangePickerProps {
  defaultValue?: TimePeriod;
  defaultStart?: Timestamp;
  defaultEnd?: Timestamp;
  intervalUnits?: IntervalUnits;
  allowCustom?: boolean | AccessLevel | null;
  fixAnchorToNow?: boolean;
  relativeTimeDirection?: 'future' | 'past';
  maxInterval?: number;
}

export interface DateTimeRangeState extends UseDateTimeRangePickerProps {
  startDate: string; // ISO string start of range
  endDate: string; // ISO string end of range
  value: TimePeriod; // The selected time period
  interval: number; // Total range duration in intervalUnits
  step: number;
  setTimePeriod: (value: TimePeriod) => void;
  setStartDate: (value: string) => void;
  setEndDate: (value: string) => void;
  refreshNow: () => void;
  reset: () => void;

  intervalUnits: IntervalUnits;
  fixAnchorToNow: boolean;
  allowCustom?: boolean | AccessLevel | null;
  relativeTimeDirection: 'future' | 'past';
}

export function useDateTimeRangePicker({
  defaultValue = TimePeriod.OneHour,
  defaultStart: defaultStartProp = now().minus(
    defaultValue === TimePeriod.Custom ? { days: 1 } : { seconds: defaultValue },
  ),
  defaultEnd: defaultEndProp = now(),
  intervalUnits = IntervalUnits.Seconds,
  allowCustom = null,
  fixAnchorToNow = true,
  relativeTimeDirection = 'past',
  maxInterval,
}: UseDateTimeRangePickerProps): DateTimeRangeState {
  const defaultStart = useMemo(
    () => stripDateTimeForInput(parseDateTime(defaultStartProp)!)!,
    [defaultStartProp],
  );
  const defaultEnd = useMemo(
    () => stripDateTimeForInput(parseDateTime(defaultEndProp)!)!,
    [defaultEndProp],
  );

  const [timePeriod, setTimePeriod] = useDateTimeRangeValue('timePeriod', defaultValue);

  const [startDate, setStartDate] = useDateTimeRangeValue('startDate', defaultStart);
  const [endDate, setEndDate] = useDateTimeRangeValue('endDate', defaultEnd);
  const [anchorNow, setAnchorNow] = useState(() => now().startOf('minute'));

  useEffect(() => {
    setAnchorNow(DateTime.now().startOf('minute'));
  }, [timePeriod]);

  const refreshNow = useCallback(() => {
    setAnchorNow(DateTime.now().startOf('minute'));
  }, []);

  const { calculatedStartDate, calculatedEndDate } = useMemo(() => {
    if (typeof timePeriod === 'number') {
      if (relativeTimeDirection === 'future') {
        return {
          calculatedStartDate: formatToISO(anchorNow)!,
          calculatedEndDate: formatToISO(anchorNow.plus({ seconds: timePeriod }))!,
        };
      }
      return {
        calculatedStartDate: formatToISO(anchorNow.minus({ seconds: timePeriod }))!,
        calculatedEndDate: formatToISO(anchorNow)!,
      };
    }

    let startISO = DateTime.fromISO(startDate);
    let endISO = DateTime.fromISO(endDate);
    if (fixAnchorToNow) {
      if (relativeTimeDirection === 'past') {
        endISO = DateTime.min(endISO, anchorNow);
      } else {
        startISO = DateTime.max(startISO, anchorNow);
      }
    }
    return {
      calculatedStartDate: formatToISO(startISO)!,
      calculatedEndDate: formatToISO(endISO)!,
    };
  }, [timePeriod, startDate, endDate, fixAnchorToNow, anchorNow, relativeTimeDirection]);

  const interval = useMemo(() => {
    if (timePeriod === TimePeriod.Custom) {
      if (!allowCustom) return getDuration(defaultValue, intervalUnits);

      const startDateCalc = DateTime.fromISO(startDate);
      const endDateCalc = DateTime.fromISO(endDate);
      const diff = endDateCalc.diff(startDateCalc, [intervalUnits]);
      return diff.toObject()[intervalUnits] ?? 0;
    }

    return getDuration(timePeriod, intervalUnits);
  }, [startDate, endDate, timePeriod, allowCustom, defaultValue, intervalUnits]);

  const clampedInterval = maxInterval != null ? Math.min(interval, maxInterval) : interval;
  const step = getStep(clampedInterval);

  const reset = useCallback(() => {
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setTimePeriod(defaultValue);
    setAnchorNow(DateTime.now().startOf('minute'));
  }, [
    setStartDate,
    setEndDate,
    setTimePeriod,
    setAnchorNow,
    defaultStart,
    defaultEnd,
    defaultValue,
  ]);

  return useMemo(
    () => ({
      defaultValue,
      defaultStart,
      defaultEnd,
      intervalUnits,
      allowCustom,
      fixAnchorToNow,
      relativeTimeDirection,
      startDate: calculatedStartDate,
      endDate: calculatedEndDate,
      value: timePeriod,
      interval: clampedInterval,
      step,
      setStartDate,
      setEndDate,
      setTimePeriod,
      reset,
      refreshNow,
    }),
    [
      defaultValue,
      defaultStart,
      defaultEnd,
      intervalUnits,
      allowCustom,
      fixAnchorToNow,
      relativeTimeDirection,
      calculatedStartDate,
      calculatedEndDate,
      timePeriod,
      clampedInterval,
      step,
      setStartDate,
      setEndDate,
      setTimePeriod,
      reset,
      refreshNow,
    ],
  );
}

type TimePeriodOption = { accessLevel?: AccessLevelProp | null | undefined; value: TimePeriod };

type ChildrenOrOptions =
  | {
      children: ReactNode;
      options?: never;
    }
  | {
      children?: never;
      options: (TimePeriod | TimePeriodOption)[];
    };

type DateTimeRangePickerDropdownContentsProps = {
  state: DateTimeRangeState;
  maxDuration?: DurationLike;
  onValueChange?: () => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  setIsOpen?: Dispatch<boolean>;
  timeZone?: string;
} & ChildrenOrOptions;

export type DateTimeRangePickerProps = Omit<DateTimeRangePickerDropdownContentsProps, 'state'> & {
  state?: DateTimeRangeState;
  size?: ControlSize;
  width?: ButtonProps['width'];
} & ChildrenOrOptions;

function normalizeOptions(options: (TimePeriod | TimePeriodOption)[]): TimePeriodOption[] {
  return options
    .map((option) => (typeof option === 'object' ? option : { value: option }))
    .filter((option) => option.accessLevel !== null);
}

const RangeLabelHyphen = styled(Text, {
  color: colors.gray200,

  [darkThemeSelector]: {
    color: colors.gray400,
  },
});
RangeLabelHyphen.displayName = 'RangeLabelHyphen';

type PickerSegment = 'presets' | 'custom';

export function DateTimeRangePickerDropdownContents({
  state: {
    setStartDate,
    setEndDate,
    setTimePeriod,
    startDate,
    endDate,
    interval,
    intervalUnits,
    value: dateTimeRangePickerValue,
    allowCustom,
    relativeTimeDirection,
  },
  children,
  maxDuration,
  options,
  onValueChange,
  onKeyDown,
  setIsOpen,
  timeZone,
}: DateTimeRangePickerDropdownContentsProps) {
  const normalizedOptions = useMemo(
    () => (options ? normalizeOptions(options) : undefined),
    [options],
  );

  const intervalSeconds = useMemo(
    () => intervalUnitsToSeconds[intervalUnits] * interval,
    [interval, intervalUnits],
  );

  useEffect(() => {
    if (
      dateTimeRangePickerValue === TimePeriod.Custom ||
      !normalizedOptions ||
      normalizedOptions.some((opt) => opt.value === intervalSeconds)
    )
      return;

    const descendingOptions = normalizedOptions
      .filter((o): o is typeof o & { value: number } => typeof o.value === 'number')
      .sort((a, b) => b.value - a.value);

    setTimePeriod(
      (descendingOptions.find((option) => option.value <= intervalSeconds) ?? normalizedOptions[0])
        .value,
    );
  }, [normalizedOptions, intervalSeconds, setTimePeriod, dateTimeRangePickerValue]);

  const [activeSegment, setActiveSegment] = useState<PickerSegment>(
    dateTimeRangePickerValue === TimePeriod.Custom ? 'custom' : 'presets',
  );

  const [startDateLocal, setStartDateLocal] = useState<string>(
    stripDateForInput(parseDateTime(startDate)!),
  );
  const [startTimeLocal, setStartTimeLocal] = useState<string>(
    formatDateTime(startDate, TimeFormat['24_HOUR_MINUTES']),
  );
  const [endDateLocal, setEndDateLocal] = useState<string>(
    stripDateForInput(parseDateTime(endDate)!),
  );
  const [endTimeLocal, setEndTimeLocal] = useState<string>(
    formatDateTime(endDate, TimeFormat['24_HOUR_MINUTES']),
  );

  // Keep local inputs in sync when external values change (e.g., presets or navigation)
  useEffect(() => {
    setStartDateLocal(stripDateForInput(parseDateTime(startDate)!));
    setStartTimeLocal(formatDateTime(startDate, TimeFormat['24_HOUR_MINUTES']));
  }, [startDate]);
  useEffect(() => {
    setEndDateLocal(stripDateForInput(parseDateTime(endDate)!));
    setEndTimeLocal(formatDateTime(endDate, TimeFormat['24_HOUR_MINUTES']));
  }, [endDate]);

  const nextStart = useMemo(
    () =>
      DateTime.fromISO(`${startDateLocal}T${startTimeLocal}`).setZone(timeZone, {
        keepLocalTime: true,
      }),
    [startDateLocal, startTimeLocal, timeZone],
  );

  const nextEnd = useMemo(
    () =>
      DateTime.fromISO(`${endDateLocal}T${endTimeLocal}`).setZone(timeZone, {
        keepLocalTime: true,
      }),
    [endDateLocal, endTimeLocal, timeZone],
  );

  const customErrorMessages = useMemo(() => {
    const errors = [];

    if (!nextStart.isValid) errors.push('Please provide a valid start date.');
    if (!nextEnd.isValid) errors.push('Please provide a valid end date.');
    if (nextStart.valueOf() > nextEnd.valueOf()) errors.push('End date must be after start date.');
    if (
      maxDuration != null &&
      nextEnd.diff(nextStart).toMillis() > Duration.fromDurationLike(maxDuration).toMillis()
    ) {
      errors.push(
        `Date range must be shorter than ${Duration.fromDurationLike(maxDuration).toHuman()}.`,
      );
    }

    if (!errors.length) return null;

    return errors;
  }, [nextStart, nextEnd, maxDuration]);

  const isDisabled = useMemo(() => {
    if (customErrorMessages?.length) return true;

    const nextStartISO = nextStart.toISO();
    const nextEndISO = nextEnd.toISO();

    return nextStartISO === startDate && nextEndISO === endDate;
  }, [customErrorMessages?.length, endDate, nextEnd, nextStart, startDate]);

  const handleApply = useCallback(() => {
    if (!nextStart.isValid || !nextEnd.isValid) return;
    if (isDisabled) return;

    // validity checked above
    const nextStartISO = nextStart.toISO()!;
    const nextEndISO = nextEnd.toISO()!;

    onValueChange?.();
    setStartDate(nextStartISO);
    setEndDate(nextEndISO);
    setTimePeriod(TimePeriod.Custom);
    setIsOpen?.(false);
  }, [
    nextStart,
    nextEnd,
    isDisabled,
    onValueChange,
    setStartDate,
    setEndDate,
    setTimePeriod,
    setIsOpen,
  ]);

  return (
    <>
      {allowCustom && (
        <DropdownMenuGroup>
          <Segments
            direction="row"
            proportions="equal"
            size="small"
            segments={[
              {
                label: 'Presets',
                selected: activeSegment === 'presets',
                onClick: () => setActiveSegment('presets'),
              },
              {
                label: 'Range',
                accessLevel: typeof allowCustom !== 'boolean' ? allowCustom : undefined,
                selected: activeSegment === 'custom',
                onClick: () => setActiveSegment('custom'),
              },
            ]}
          />
        </DropdownMenuGroup>
      )}
      {activeSegment === 'presets' && (
        <DropdownMenuRadioGroup
          label={`Filter by ${relativeTimeDirection}`}
          value={dateTimeRangePickerValue.toString()}
          onValueChange={(val: string) => {
            let value = TimePeriod.OneHour;
            if (val === TimePeriod.Custom) {
              value = val;
            }
            const num = Number.parseInt(val, 10);
            if (
              !Number.isNaN(num) &&
              Number.isInteger(num) &&
              Object.values(TimePeriod).includes(num)
            ) {
              value = num;
            }
            setTimePeriod(value);
          }}
        >
          {children ||
            normalizedOptions?.map((option) => (
              <DropdownMenuRadioItem
                key={option.value}
                value={option.value.toString()}
                accessLevel={option.accessLevel}
                onKeyDown={onKeyDown}
              >
                {timePeriodLabel(option.value)}
              </DropdownMenuRadioItem>
            ))}
        </DropdownMenuRadioGroup>
      )}
      {allowCustom && activeSegment === 'custom' && (
        <>
          <DropdownMenuGroup>
            <VStack align="center" spacing={8}>
              <ControlGroup direction="row" size="small">
                <TextInput
                  type="date"
                  aria-label="Start date"
                  onChange={setStartDateLocal}
                  value={startDateLocal}
                  width="full"
                  onKeyDown={onKeyDown}
                />
                <TextInput
                  type="time"
                  aria-label="Start time"
                  onChange={setStartTimeLocal}
                  value={startTimeLocal}
                  width="full"
                />
              </ControlGroup>
              <Icon icon="arrow-down" size={14} variant="neutral" />
              <ControlGroup direction="row" size="small">
                <TextInput
                  type="date"
                  aria-label="End date"
                  onChange={setEndDateLocal}
                  value={endDateLocal}
                  width="full"
                  onKeyDown={onKeyDown}
                />
                <TextInput
                  type="time"
                  aria-label="End time"
                  onChange={setEndTimeLocal}
                  value={endTimeLocal}
                  width="full"
                  onKeyDown={onKeyDown}
                />
              </ControlGroup>
              {!!customErrorMessages?.length && (
                <Alert
                  type="inline"
                  variant="negative"
                  copy={
                    <VStack spacing={4}>
                      {customErrorMessages.map((err, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Text key={`${index}:${err}`}>{err}</Text>
                      ))}
                    </VStack>
                  }
                />
              )}
            </VStack>
          </DropdownMenuGroup>
          <DropdownMenuGroup>
            <Button size="small" variant="secondary" disabled={isDisabled} onClick={handleApply}>
              Apply
            </Button>
          </DropdownMenuGroup>
        </>
      )}
    </>
  );
}

export function DateTimeRangePicker({
  size,
  width,

  state: stateProp,

  ...props
}: DateTimeRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  const stateContext = useDateTimeRangeStateContextOrNull();

  const state = checkDefinedOrThrow(
    stateProp ?? stateContext,
    new Error('DateTimeRangePicker must be provided a state or used in a DateTimeRangeProvider'),
  );

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuButton
        size={size}
        variant="secondary"
        arrangement="leading-icon"
        icon="clock"
        width={width}
      >
        {state.value === 'CUSTOM' ? (
          <Text>
            <FormatDateTime value={state.startDate} format={TimeFormat.MEDIUM} />{' '}
            <RangeLabelHyphen>–</RangeLabelHyphen>{' '}
            <FormatDateTime value={state.endDate} format={TimeFormat.MEDIUM} />
          </Text>
        ) : (
          timePeriodLabel(state.value)
        )}
      </DropdownMenuButton>
      <DropdownMenuPopover align="start" side="bottom" sideOffset={6} width={240}>
        <DateTimeRangePickerDropdownContents state={state} {...props} />
      </DropdownMenuPopover>
    </DropdownMenu>
  );
}
